import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  Chip,
  Backdrop,
  Container,
  Grid,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Login,
  Brightness4,
  Brightness7,
  Language,
  Security,
  Fingerprint,
  Face,
  BusinessCenter,
  Schedule,
  Analytics,
  People,
  Monitor,
} from '@mui/icons-material';

import { useAuth } from '../../contexts/AuthContext';
import { useColorScheme } from '@mui/material/styles';

const LoginPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { mode, setMode } = useColorScheme();

  const [formData, setFormData] = useState({ 
    username: 'admin', 
    password: 'admin123' 
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    try {
      const result = await login({ username: formData.username, password: formData.password });
      if (result) {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(t('auth.loginError'));
    }
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const toggleColorMode = () => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  };

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'ru' ? 'en' : 'ru');
  };

  // Адаптивные размеры
  const getCardWidth = () => {
    if (isMobile) return '100%';
    if (isTablet) return '70%';
    return '50%'; // Desktop
  };

  const getMaxWidth = () => {
    if (isMobile) return 400;
    if (isTablet) return 500;
    return 600; // Desktop
  };

  // Функции возможностей с иконками
  const getFeatures = () => [
    { key: 'media', icon: BusinessCenter },
    { key: 'monitoring', icon: Monitor },
    { key: 'scheduling', icon: Schedule },
    { key: 'users', icon: People },
    { key: 'analytics', icon: Analytics }
  ];

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const pulseVariants = {
    scale: [1, 1.08, 1],
    boxShadow: [
      '0 10px 30px rgba(102, 126, 234, 0.4)',
      '0 20px 60px rgba(102, 126, 234, 0.8)',
      '0 10px 30px rgba(102, 126, 234, 0.4)'
    ],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const featureItemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: 1.2 + i * 0.15,
        duration: 0.6,
        ease: "easeOut"
      }
    })
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #0c0c0c 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #533483 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: isDesktop ? 'row' : 'column',
        p: { xs: 2, md: 4 },
      }}
    >
      {/* Улучшенный анимированный фон */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        {/* Основные частицы */}
        {[...Array(isDesktop ? 40 : 25)].map((_, i) => (
          <motion.div
            key={`particle-${i}`}
            initial={{ 
              x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920),
              y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1080),
              opacity: 0
            }}
            animate={{
              x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920),
              y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1080),
              opacity: [0, 0.8, 0]
            }}
            transition={{
              duration: Math.random() * 15 + 15,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              position: 'absolute',
              width: Math.random() * 6 + 2,
              height: Math.random() * 6 + 2,
              backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : '#ffffff',
              borderRadius: '50%',
              filter: 'blur(1px)',
              boxShadow: '0 0 20px rgba(255, 255, 255, 0.5)'
            }}
          />
        ))}
        
        {/* Дополнительные светящиеся орбы */}
        {[...Array(isDesktop ? 8 : 5)].map((_, i) => (
          <motion.div
            key={`orb-${i}`}
            initial={{ 
              x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920),
              y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1080),
              scale: 0
            }}
            animate={{
              x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920),
              y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1080),
              scale: [0, 1, 0],
              opacity: [0, 0.3, 0]
            }}
            transition={{
              duration: Math.random() * 20 + 20,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            style={{
              position: 'absolute',
              width: Math.random() * 200 + 100,
              height: Math.random() * 200 + 100,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${theme.palette.mode === 'dark' 
                ? 'rgba(102, 126, 234, 0.2)' 
                : 'rgba(255, 255, 255, 0.3)'} 0%, transparent 70%)`,
              filter: 'blur(60px)',
            }}
          />
        ))}
      </Box>

      {/* Улучшенные контролы в углу */}
      <motion.div
        initial={{ opacity: 0, x: 50, y: -20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          zIndex: 10,
        }}
      >
        <Stack direction="row" spacing={1.5}>
          <motion.div
            whileHover={{ scale: 1.15, rotate: 180 }}
            whileTap={{ scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            <IconButton
              onClick={toggleColorMode}
              sx={{
                backdropFilter: 'blur(25px)',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                color: 'white',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                },
              }}
            >
              {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.15, rotate: 15 }}
            whileTap={{ scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            <IconButton
              onClick={toggleLanguage}
              sx={{
                backdropFilter: 'blur(25px)',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                color: 'white',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                },
              }}
            >
              <Language />
            </IconButton>
          </motion.div>
        </Stack>
      </motion.div>

      {/* Улучшенная левая панель (только на desktop) */}
      {isDesktop && (
        <motion.div
          initial={{ opacity: 0, x: -150 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 1 }}
          style={{ 
            flex: 1, 
            marginRight: 50,
            zIndex: 5,
            maxWidth: '55%'
          }}
        >
          <Box sx={{ color: 'white', textAlign: 'left' }}>
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <Typography 
                variant="h1" 
                sx={{ 
                  fontWeight: 900, 
                  mb: 3,
                  fontSize: '3.5rem',
                  background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  textShadow: '0 0 50px rgba(255,255,255,0.4)',
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em'
                }}
              >
                {t('app.title')}
              </Typography>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
            >
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: 5, 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 300,
                  lineHeight: 1.4,
                  letterSpacing: '0.01em'
                }}
              >
                {t('app.subtitle')}
              </Typography>
            </motion.div>
            
            <Box sx={{ mb: 4 }}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1, duration: 0.6 }}
              >
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: 'white', 
                    mb: 3, 
                    fontWeight: 700,
                    textShadow: '0 2px 10px rgba(0,0,0,0.3)'
                  }}
                >
                  {t('app.features.title')}
                </Typography>
              </motion.div>
              
              <Stack spacing={2}>
                {getFeatures().map((feature, index) => {
                  const IconComponent = feature.icon;
                  return (
                    <motion.div
                      key={feature.key}
                      custom={index}
                      variants={featureItemVariants}
                      initial="hidden"
                      animate="visible"
                      whileHover={{ 
                        scale: 1.05, 
                        x: 20,
                        transition: { duration: 0.3 }
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 2,
                          borderRadius: 3,
                          backdropFilter: 'blur(20px)',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                          }
                        }}
                      >
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                          }}
                        >
                          <IconComponent sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontWeight: 500,
                            fontSize: '1.1rem'
                          }}
                        >
                          {t(`app.features.${feature.key}`)}
                        </Typography>
                      </Box>
                    </motion.div>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        </motion.div>
      )}

      {/* Улучшенная главная карточка логина */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ 
          zIndex: 5, 
          width: getCardWidth(),
          maxWidth: getMaxWidth(),
          flex: isDesktop ? '0 0 auto' : 1
        }}
      >
        <Card
          sx={{
            backdropFilter: 'blur(40px)',
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(15, 15, 25, 0.7)' 
              : 'rgba(255, 255, 255, 0.5)',
            border: `2px solid ${theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.15)' 
              : 'rgba(255, 255, 255, 0.6)'}`,
            borderRadius: isDesktop ? 8 : 6,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 35px 80px -15px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              : '0 35px 80px -15px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
            p: { xs: 4, md: 6, lg: 7 },
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
              animation: 'shimmer 4s infinite',
            },
            '@keyframes shimmer': {
              '0%': { left: '-100%' },
              '100%': { left: '100%' },
            },
          }}
        >
          {/* Улучшенный заголовок */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
              <motion.div
                animate={pulseVariants}
                style={{
                  display: 'inline-block',
                  marginBottom: 20,
                }}
              >
                <Box
                  sx={{
                    width: { xs: 80, md: 90, lg: 100 },
                    height: { xs: 80, md: 90, lg: 100 },
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: -2,
                      left: -2,
                      right: -2,
                      bottom: -2,
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #667eea, #764ba2, #f093fb, #667eea)',
                      zIndex: -1,
                      filter: 'blur(10px)',
                      opacity: 0.7,
                    }
                  }}
                >
                  <Security sx={{ fontSize: { xs: 40, md: 45, lg: 50 }, color: 'white' }} />
                </Box>
              </motion.div>

              <Typography 
                variant={isDesktop ? "h3" : "h4"} 
                sx={{ 
                  fontWeight: 800, 
                  mb: 2,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)'
                    : 'linear-gradient(45deg, #1a1a1a 30%, #333333 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  textShadow: theme.palette.mode === 'dark' 
                    ? '0 2px 20px rgba(255,255,255,0.3)'
                    : '0 2px 20px rgba(0,0,0,0.3)',
                }}
              >
                {t('auth.welcome')}
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.8)' 
                    : 'rgba(0, 0, 0, 0.8)',
                  fontWeight: 400,
                  opacity: 0.9
                }}
              >
                {t('auth.signIn')}
              </Typography>
            </Box>
          </motion.div>

          {/* Остальная часть формы остается без изменений */}
          {/* Форма логина */}
          <motion.div variants={itemVariants}>
            {/* Form fields with improved styling */}
            <Box>
              <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label={t('auth.username')}
                  value={formData.username}
                  onChange={handleInputChange('username')}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backdropFilter: 'blur(20px)', // Increased blur
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(255, 255, 255, 0.7)',
                      borderRadius: 2,
                      boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.15)'
                          : 'rgba(255, 255, 255, 0.8)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.2)'
                          : 'rgba(255, 255, 255, 0.9)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                      },
                    },
                    '& .MuiInputLabel-root': { // Label color
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.9)'
                        : 'rgba(0, 0, 0, 0.9)',
                      fontWeight: 500,
                    },
                    '& .MuiOutlinedInput-input': { // Input text color
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.95)'
                        : 'rgba(0, 0, 0, 0.95)',
                      fontWeight: 500,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label={t('auth.password')}
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            color: theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.7)'
                              : 'rgba(0, 0, 0, 0.7)'
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      backdropFilter: 'blur(20px)', // Increased blur
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(255, 255, 255, 0.7)',
                      borderRadius: 2,
                      boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.15)'
                          : 'rgba(255, 255, 255, 0.8)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.2)'
                          : 'rgba(255, 255, 255, 0.9)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                      },
                    },
                    '& .MuiInputLabel-root': { // Label color
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.9)'
                        : 'rgba(0, 0, 0, 0.9)',
                      fontWeight: 500,
                    },
                    '& .MuiOutlinedInput-input': { // Input text color
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.95)'
                        : 'rgba(0, 0, 0, 0.95)',
                      fontWeight: 500,
                    },
                  }}
                />

                {/* Error display */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }} // Adjusted animation
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Alert
                        severity="error"
                        sx={{
                          mb: 2, // Added margin
                          backgroundColor: 'rgba(211, 47, 47, 0.1)', // Adjusted color
                          backdropFilter: 'blur(20px)', // Added blur
                          border: '1px solid rgba(211, 47, 47, 0.3)', // Adjusted border
                          color: theme.palette.mode === 'dark' ? '#ffcdd2' : '#d32f2f', // Adjusted text color
                          '& .MuiAlert-icon': { // Icon color
                            color: theme.palette.mode === 'dark' ? '#ffcdd2' : '#d32f2f',
                          }
                        }}
                      >
                        {error}
                      </Alert>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Login Button - упрощено без motion */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size={isDesktop ? "large" : "medium"} // Adaptive size
                  disabled={isLoading}
                  startIcon={<Login />}
                  sx={{
                    py: { xs: 1.5, md: 2 }, // Adaptive padding
                    fontSize: { xs: '1rem', md: '1.1rem' }, // Adaptive font size
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)', // Adjusted shadow
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
                      transition: 'left 0.5s',
                    },
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      boxShadow: '0 8px 30px rgba(102, 126, 234, 0.6)', // Adjusted hover shadow
                      transform: 'translateY(-2px)',
                      '&::before': {
                        left: '100%',
                      },
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  {isLoading ? t('common.loading') : t('auth.loginButton')}
                </Button>
              </Box>
            </Box>
          </motion.div>

          {/* Разделитель */}
          <motion.div variants={itemVariants}>
            <Divider sx={{ mb: 3 }}>
              <Chip 
                label={t('auth.or')} 
                sx={{ 
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(255, 255, 255, 0.8)',
                  color: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.8)' 
                    : 'rgba(0, 0, 0, 0.8)',
                  backdropFilter: 'blur(20px)',
                  fontWeight: 500
                }}
              />
            </Divider>
          </motion.div>

          {/* Альтернативные способы входа */}
          <motion.div variants={itemVariants}>
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ flex: 1 }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Fingerprint />}
                  sx={{
                    py: 1.5,
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.8)' 
                      : 'rgba(0, 0, 0, 0.8)',
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.05)' 
                      : 'rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(20px)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.1)' 
                        : 'rgba(255, 255, 255, 0.5)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {t('auth.touchId')}
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ flex: 1 }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Face />}
                  sx={{
                    py: 1.5,
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.8)' 
                      : 'rgba(0, 0, 0, 0.8)',
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.05)' 
                      : 'rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(20px)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.1)' 
                        : 'rgba(255, 255, 255, 0.5)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {t('auth.faceId')}
                </Button>
              </motion.div>
            </Stack>
          </motion.div>

          {/* Демо данные */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(76, 175, 80, 0.1)' 
                  : 'rgba(76, 175, 80, 0.1)',
                border: '1px solid rgba(76, 175, 80, 0.3)',
                backdropFilter: 'blur(20px)',
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.9)' 
                    : 'rgba(0, 0, 0, 0.9)',
                  fontWeight: 500,
                  display: 'block',
                  mb: 1
                }}
              >
                {t('auth.demoCredentials')}:
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.8)' 
                    : 'rgba(0, 0, 0, 0.8)',
                  fontFamily: 'monospace'
                }}
              >
                👤 <strong>admin</strong> / 🔑 <strong>admin123</strong>
              </Typography>
            </Box>
          </motion.div>
        </Card>
      </motion.div>

      {/* Загрузочный экран */}
      <AnimatePresence>
        {isLoading && (
          <Backdrop
            open={isLoading}
            sx={{
              zIndex: 9999,
              backdropFilter: 'blur(20px)',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            }}
          >
            <motion.div
              initial={{ scale: 0, rotate: 0 }}
              animate={{ scale: 1, rotate: 360 }}
              exit={{ scale: 0, rotate: 0 }}
              transition={{
                rotate: { duration: 1, repeat: Infinity, ease: "linear" },
                scale: { duration: 0.3 }
              }}
            >
              <Box
                sx={{
                  width: { xs: 70, md: 80 },
                  height: { xs: 70, md: 80 },
                  borderRadius: '50%',
                  background: 'conic-gradient(from 0deg, #667eea, #764ba2, #667eea)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: { xs: 50, md: 60 },
                    height: { xs: 50, md: 60 },
                    borderRadius: '50%',
                    backgroundColor: theme.palette.background.default,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Login sx={{ color: 'primary.main', fontSize: { xs: 25, md: 30 } }} />
                </Box>
              </Box>
            </motion.div>
          </Backdrop>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default LoginPage; 